import React, { useCallback, useEffect, useState } from 'react';
import { Edit, KeyboardArrowDown, KeyboardArrowUp, Search } from '@material-ui/icons';
import Box from '@material-ui/core/Box';
import TableContainer from '@material-ui/core/TableContainer';
import { useSnackbar } from 'notistack';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import IconButton from '@material-ui/core/IconButton';
import { useHistory } from 'react-router';
import {
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import TablePagination from 'components/atoms/TablePagination/TablePagination';
import { DictionaryDto } from 'api/dictionary';
import Spin from 'components/atoms/Spin/Spin';
import { fetchRegionsDict } from 'api/regions';
import { BranchDto, fetchBranchesHierarchy, getBranches } from '../../api/branches';

const getColorByChildNum = (childNum: string | undefined) => {
    switch (childNum) {
        case '2':
            return '#d4edda';
        case '3':
            return '#f8d7da';
        default:
            return '#fff';
    }
};
const getBorderColorByChildNum = (childNum: string | undefined) => {
    switch (childNum) {
        case '2':
            return '2px solid #cfc8b8';
        case '3':
            return '2px solid #bfbdba';
        default:
            return 'none';
    }
};

const Row = (props: { item: BranchDto; childNum: string }) => {
    const { item, childNum } = props;

    const history = useHistory();
    const [isOpen, setOpen] = useState(false);
    const [children, setChildren] = useState<BranchDto[]>([]);
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const handleExpandClick = useCallback(
        async (e) => {
            e.preventDefault();
            setOpen(!isOpen);
            if (children.length === 0) {
                try {
                    setLoading(true);
                    const { data } = await fetchBranchesHierarchy(item.code, null);
                    setChildren(data);
                } catch {
                    enqueueSnackbar('Ошибка при получении данных', { variant: 'error' });
                } finally {
                    setLoading(false);
                }
            }
        },
        [isOpen, enqueueSnackbar, children.length, item.code]
    );

    const handleEdit = (id: number | undefined) => {
        history.push(`/cabinet/branch-details/${id}`);
    };
    return (
        <>
            <TableRow
                hover={true}
                style={{
                    background: getColorByChildNum(childNum),
                    borderBottom: getBorderColorByChildNum(childNum),
                }}
            >
                <TableCell>
                    {item.has_children && (
                        <IconButton onClick={handleExpandClick}>
                            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell>{item.code}</TableCell>
                <TableCell>{item.title}</TableCell>
                <TableCell>{item.address}</TableCell>
                {/*<TableCell>{item.region?.description}</TableCell>*/}
                <TableCell>{item.is_open ? 'Да' : 'Нет'}</TableCell>
                <TableCell>
                    <IconButton onClick={() => handleEdit(item.id)}>
                        <Edit />
                    </IconButton>
                </TableCell>
            </TableRow>
            {item.has_children &&
                isOpen &&
                children.length > 0 &&
                children.map((child) => {
                    return (
                        <Row item={child} key={child.code} childNum={`${Number(childNum) + 1}`} />
                    );
                })}
            {loading && (
                <TableRow key={`spinner_${item.code}`}>
                    <TableCell colSpan={12} style={{ textAlign: 'center' }}>
                        <CircularProgress />
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

const Branches = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    // Table and stuff
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<BranchDto[]>([]);

    // Pagination
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [total, setTotal] = useState(0);
    // const [order, setOrder] = React.useState<Order>('asc');
    // const [orderBy, setOrderBy] = React.useState<keyof DictionaryDto>('id');
    const [search, setSearch] = React.useState<string | null>(null);
    const [regions, setRegions] = useState<DictionaryDto[]>([]);
    const [regionCode, setRegionCode] = useState<string | null>('');

    // Fetch
    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            if (search) {
                const x = await getBranches(
                    page,
                    perPage,
                    `code,asc`,
                    search,
                    regionCode === '' ? null : regionCode
                );
                setItems(x.content);
                setTotal(x.total_elements);
            } else {
                const { data, total } = await fetchBranchesHierarchy(
                    null,
                    regionCode === '' ? null : regionCode
                );
                setItems(data);
                setTotal(total);
            }
        } catch {
            enqueueSnackbar('Ошибка при получении данных', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [
        setLoading,
        setTotal,
        setItems,
        page,
        perPage,
        enqueueSnackbar,
        // orderBy,
        // order,
        search,
        regionCode,
    ]);

    const handleAdd = () => {
        history.push('/cabinet/branch-details');
    };

    const handleSearchChange = (e: any) => {
        setSearch(e.target.value);
    };

    const handleRegionChange = (e: any) => {
        setRegionCode(e.target.value as string);
    };

    const handleSearch = () => {
        fetch();
    };

    // const handleRequestSort = (property: keyof DictionaryDto) => {
    //     const isAsc = orderBy === property && order === 'asc';
    //     setOrder(isAsc ? 'desc' : 'asc');
    //     setOrderBy(property);
    // };

    useEffect(() => {
        fetchRegionsDict().then((res) => {
            setRegions(res);
        });
        fetch();
    }, [fetch]);

    return (
        <Box position={'relative'}>
            <Grid container spacing={3}>
                <Grid item xs={4}>
                    <TextField
                        label="Введите наименование"
                        fullWidth
                        value={search}
                        variant="outlined"
                        className={classes.formControl}
                        onChange={handleSearchChange}
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormControl className={classes.formControl} variant="outlined">
                        <InputLabel id="product-id-label">Регион</InputLabel>
                        <Select
                            labelId="product-id-label"
                            id="product-id"
                            value={regionCode}
                            label="Регион"
                            onChange={handleRegionChange}
                        >
                            <MenuItem value="">
                                <em>Не выбрано</em>
                            </MenuItem>
                            {regions.map((region, index) => (
                                <MenuItem key={index} value={region.code}>
                                    {region.description}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4} className={classes.buttons}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                    >
                        Поиск
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleAdd}>
                        Добавить
                    </Button>
                </Grid>
            </Grid>
            <Box position={'relative'}>
                {loading && <Spin />}
                <TableContainer>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell>Код</TableCell>
                                <TableCell>Наименование</TableCell>
                                <TableCell>Адрес</TableCell>
                                <TableCell>Открыто</TableCell>
                                <TableCell>Действии</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, _index) => (
                                // <TableRow key={index} hover={true}>
                                //     <TableCell>{page * perPage + index + 1}</TableCell>
                                //     <TableCell>{item.code}</TableCell>
                                //     <TableCell>{item.title}</TableCell>
                                //     <TableCell>{item.address}</TableCell>
                                //     <TableCell>{item.region?.description}</TableCell>
                                //     <TableCell>{item.is_open ? 'Да' : 'Нет'}</TableCell>
                                //     <TableCell>
                                //         <IconButton onClick={() => handleEdit(item.id)}>
                                //             <Edit />
                                //         </IconButton>
                                //     </TableCell>
                                // </TableRow>
                                <Row item={item} key={item.code} childNum={'1'} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <TablePagination
                total={total}
                perPage={perPage}
                page={page}
                onChangePage={setPage}
                onChangeRowsPerPage={setPerPage}
            />
        </Box>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {},
    formControl: {
        minWidth: 120,
        width: '100%',
    },
    mt5: {
        marginTop: theme.spacing(3),
        width: '100%',
    },
    buttons: {
        '& button': {
            marginLeft: theme.spacing(2),
            '&:first-child': {
                marginLeft: 0,
            },
        },
    },
}));

export default Branches;
